import { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';

const WHOLE_NUMBER = /^\d+$/;

const useGASettingsPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);

  const [status, setStatus] = useState('loading');
  const [errorInfo, setErrorInfo] = useState(null);
  const [maxCompaniesInput, setMaxCompaniesInput] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchSettings = useCallback(() => {
    if (!config?.backendUrl) return;
    setStatus('loading');
    axios.get(`${config.backendUrl}/ga/settings`)
      .then((res) => {
        setMaxCompaniesInput(String(res.data.maxCompaniesPerUser));
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching platform settings', error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /ga/settings · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      });
  }, [config, t]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const save = async () => {
    const value = maxCompaniesInput.trim();
    if (!WHOLE_NUMBER.test(value)) {
      setFieldError(t('Enter a whole number of 0 or more', { defaultValue: 'Enter a whole number of 0 or more' }));
      return;
    }
    setFieldError('');
    setSaving(true);
    try {
      const res = await axios.put(`${config.backendUrl}/ga/settings`, { maxCompaniesPerUser: Number(value) });
      setMaxCompaniesInput(String(res.data.maxCompaniesPerUser));
      notify(t('Settings saved', { defaultValue: 'Settings saved' }));
    } catch (err) {
      console.error('Error saving platform settings', err);
      notify(err.response?.data?.error || t("Couldn't save settings", { defaultValue: "Couldn't save settings" }), 'error');
    } finally {
      setSaving(false);
    }
  };

  return {
    t, status, errorInfo, fetchSettings,
    maxCompaniesInput, setMaxCompaniesInput, fieldError,
    saving, save, snackbar, setSnackbar,
  };
};

export default useGASettingsPage;
